import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import cronLogo from '../assets/cron-labs-logo.jpeg';

const API_URL = process.env.REACT_APP_API_ENDPOINT;

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const uid = searchParams.get('uid');
  const token = searchParams.get('token');

  const validatePassword = (pass) => {
    if (pass.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/\d/.test(pass)) {
      return 'Password must contain at least one digit';
    }
    if (!/[a-zA-Z]/.test(pass)) {
      return 'Password must contain at least one letter';
    }
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      setLoading(false);
      return;
    }

    try {
      await axios.post(`${API_URL}/api/accounts/reset-password/`, {
        uid,
        token,
        password,
      });
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      console.error('Password reset error:', err);
      setError(err.response?.data?.error || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!uid || !token) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50">
        <img src={cronLogo} alt="Logo" className="w-10 mb-4 mt-4" />
        <div className="bg-white rounded-xl shadow p-8 w-full max-w-md text-center">
          <h2 className="text-xl font-bold mb-2">Invalid Reset Link</h2>
          <p className="text-gray-600 mb-4">The password reset link is invalid or has expired.</p>
          <button
            onClick={() => navigate('/login')}
            className="text-indigo-600 hover:underline"
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50">
      <img src={cronLogo} alt="Logo" className="w-10 mb-4 mt-4" />
      <form
        className="bg-white rounded-xl shadow p-8 w-full max-w-md flex flex-col gap-4"
        onSubmit={handleSubmit}
      >
        <h2 className="text-xl font-bold mb-2 text-center">Reset your password</h2>
        <p className="text-sm text-gray-600 text-center mb-4">
          Please enter your new password below.
        </p>
        <div>
          <label className="block font-semibold mb-1">New Password</label>
          <input
            type="password"
            className="border rounded px-3 py-2 w-full"
            placeholder="Enter new password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
            minLength={8}
          />
          <p className="text-xs text-gray-500 mt-1">
            Password must be at least 8 characters long and contain at least one letter and one digit.
          </p>
        </div>
        <div>
          <label className="block font-semibold mb-1">Confirm Password</label>
          <input
            type="password"
            className="border rounded px-3 py-2 w-full"
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            required
            minLength={8}
          />
        </div>
        {error && <div className="text-red-500 text-sm text-center">{error}</div>}
        {success && (
          <div className="text-green-500 text-sm text-center">
            Password reset successful! Redirecting to login...
          </div>
        )}
        <button
          type="submit"
          className="bg-indigo-600 text-white rounded px-6 py-2 w-full mt-2 font-semibold disabled:opacity-60"
          disabled={loading}
        >
          {loading ? 'Resetting...' : 'Reset Password'}
        </button>
        <button
          type="button"
          onClick={() => navigate('/login')}
          className="text-indigo-600 hover:underline text-sm"
        >
          Back to Login
        </button>
      </form>
    </div>
  );
};

export default ResetPassword; 